import { FC, useMemo, useState } from 'react';

import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { PriceSimulationProductCombinationOptionDto, PromoDto } from '@eneco/dc-be/client/types.gen';
import { useFormatter } from '@eneco/i18n';
import { AlertDialog, Badge, Bucket, Heading, IconButton, Stack, Text, useMediaQuery } from '@eneco/sparky';
import { InfoIcon } from '@eneco/sparky/icons';
import { styled } from '@eneco/sparky/stitches';
import { useContent } from '@sitecore/common';
import { OrderManagementWithItems } from '@sitecore/types/manual/be/OrderManagementWithItems';
import { useRouter } from 'next/router';
import { useFormContext } from 'react-hook-form';
import { useSWRConfig } from 'swr';

import {
  ChangeProductLink,
  ChangeServiceLink,
  RateCardLink,
  RemoveProductLink,
  RemoveServiceLink,
} from './SelectedProductActionLinks';
import DataErrorNotification from '../../../components/DataErrorNotification';
import { styledStackItemClass } from '../../../styles/components.css';
import { getSitecoreBundle, getSitecoreProduct } from '../../../utils/getSitecoreProduct';
import { isValidGezinsbondNumber } from '../../../utils/isValidGezinsbondNumber';
import { useOrderId } from '../hooks/useOrderId';
import { useSelectedProducts } from '../hooks/useSelectedProducts';
import { OrderManagementFormSchema } from '../types/orderManagementSchema';
import { orderManagementSteps } from '../types/OrderManagementSteps';
import { clearSelectedProducts, clearSelectedService } from '../utils/orderManagementStorage';
import useCheckLossOfPromo from '../utils/useCheckLossOfPromo';
import { getVatPercentage } from '../utils/vatPercentage';

const StyledBadge = styled(Badge, {
  borderRadius: '12px !important',
});

const PromoBadges: FC<{
  hasPriceSimulationPromo: boolean;
  hasValidGezinsbondNumber: boolean;
  gezinsbondPromoBadgeText: string | undefined;
  orderPromo: PromoDto[] | null | undefined;
  allPromoTexts: string[];
  allPromoDisclaimers: Array<{ disclaimerText?: string }>;
  isInSideBar: boolean;
}> = ({
  hasPriceSimulationPromo,
  hasValidGezinsbondNumber,
  gezinsbondPromoBadgeText,
  orderPromo,
  allPromoTexts,
  allPromoDisclaimers,
  isInSideBar,
}) => {
  if (!hasPriceSimulationPromo && !hasValidGezinsbondNumber) {
    return null;
  }
  return (
    <Stack direction="row" alignY="center" gap="1">
      <Stack direction="column" gap="1">
        {hasValidGezinsbondNumber && (
          <Stack direction="row" gap="1">
            <StyledBadge color="success" size="S">
              <Text whiteSpace={'normal'}>{gezinsbondPromoBadgeText}</Text>
            </StyledBadge>
          </Stack>
        )}
        {orderPromo && (
          <>
            {allPromoTexts.map((promoText, index) => (
              <Stack direction="row" gap="1" key={index} alignY="center">
                <StyledBadge color="success" size="S">
                  <Text whiteSpace={'normal'}>{promoText}</Text>
                </StyledBadge>
                {!isInSideBar && (
                  <TrackedDialog
                    trigger={
                      <IconButton label="">
                        <InfoIcon />
                      </IconButton>
                    }>
                    {allPromoDisclaimers[index]?.disclaimerText}
                  </TrackedDialog>
                )}
              </Stack>
            ))}
          </>
        )}
      </Stack>
    </Stack>
  );
};

export const SelectedProduct: FC<{
  isInSideBar?: boolean;
}> = ({ isInSideBar = false }) => {
  const { fields } = useContent<OrderManagementWithItems>();
  const { currency, format } = useFormatter();
  const { query } = useRouter();
  const { watch } = useFormContext<OrderManagementFormSchema>();
  const { mutate } = useSWRConfig();

  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [hasUpdateError, setHasUpdateError] = useState(false);

  const priceSimulationId = query.priceSimulationId as string | undefined;
  const orderId = useOrderId();

  const paymentMethod = watch('selectPaymentMethod.paymentMethod');
  const gezinsbondMembershipNumber = watch('overviewStep.gezinsbondMembershipNumber');

  const hasValidGezinsbondNumber = useMemo(() => {
    return !!(gezinsbondMembershipNumber && isValidGezinsbondNumber(gezinsbondMembershipNumber));
  }, [gezinsbondMembershipNumber]);

  const {
    isBundle,
    selectedProducts,
    selectedServices,
    cacheKey: useSelectedProductsCacheKey,
    orderSimulateError,
    orderPromos: orderPromo,
  } = useSelectedProducts({
    orderId,
  });
  const { hasLossOfPromo } = useCheckLossOfPromo({ paymentMethod });
  const { hasPriceSimulationPromo, vatPercentage, sitecoreBundle, sitecoreProduct, totalPrice } = useProductData({
    fields,
    hasLossOfPromo,
    selectedProducts,
    isBundle,
  });

  if (!selectedProducts) {
    if (orderSimulateError || hasUpdateError) {
      return <DataErrorNotification />;
    }
    return null;
  }

  const GEZINSBOND_PROMO_EUROS = 20;
  const displayTotalPrice = hasValidGezinsbondNumber ? totalPrice - GEZINSBOND_PROMO_EUROS : totalPrice;

  const displayName = isBundle ? sitecoreBundle?.bundleDisplayName : sitecoreProduct?.productDisplayName?.value;

  const allPromoTexts = selectedProducts.promoTexts ?? [];
  const allPromoDisclaimers = selectedProducts.promoDisclaimers ?? [];

  const elecTariffUrl = selectedProducts.electricityProduct?.tarriffChartUrl;
  const gasTariffUrl = selectedProducts.gasProduct?.tarriffChartUrl;

  const selectedProductName = isBundle
    ? [selectedProducts.electricityProduct?.productName, selectedProducts.gasProduct?.productName]
        .filter(Boolean)
        .join('|')
    : (selectedProducts.electricityProduct?.productName ?? selectedProducts.gasProduct?.productName ?? '');

  const handleRemoveClick = () => {
    setIsRemoveDialogOpen(true);
  };

  const handleConfirmRemoveProducts = async () => {
    try {
      await clearSelectedProducts({
        orderId: orderId ?? '',
        selectedServices,
        mutate,
        cacheKey: useSelectedProductsCacheKey,
      });
      setIsRemoveDialogOpen(false);
    } catch {
      setHasUpdateError(true);
    }
  };

  const handleDenyRemove = () => {
    setIsRemoveDialogOpen(false);
  };

  return (
    <>
      {(orderSimulateError || hasUpdateError) && <DataErrorNotification />}
      <AlertDialog
        isOpen={isRemoveDialogOpen}
        setOpen={setIsRemoveDialogOpen}
        title={fields.selectedProductsStep.removeProductDialog.value.title}
        description={fields.selectedProductsStep.removeProductDialog.value.content}
        confirmText={fields.selectedProductsStep.removeProductDialog.value.submitButtonText ?? 'Remove'}
        denyText={fields.selectedProductsStep.removeProductDialog.value.cancelButtonText ?? 'Cancel'}
        onConfirm={handleConfirmRemoveProducts}
        onDeny={handleDenyRemove}></AlertDialog>
      <Bucket title={fields.selectedProductsStep.selectedProductsTitle.value}>
        <Bucket.Content>
          <Stack direction="column" gap="4">
            <Stack direction="row" gap="2" alignX="justify" wrap>
              <Stack.Item className={styledStackItemClass}>
                <Stack gap="2">
                  <Heading size="3XS" as="h3">
                    {displayName}
                  </Heading>
                  <Text size="BodyS" color="textLowEmphasis">
                    {format(fields.selectedProductsStep.vatInformationLabel.value, { amount: vatPercentage })}
                  </Text>
                  {!isInSideBar && (
                    <PromoBadges
                      hasPriceSimulationPromo={hasPriceSimulationPromo}
                      hasValidGezinsbondNumber={hasValidGezinsbondNumber}
                      gezinsbondPromoBadgeText={fields.overviewStep.gezinsbondPromoBadgeText?.value}
                      orderPromo={orderPromo ?? undefined}
                      allPromoTexts={allPromoTexts}
                      allPromoDisclaimers={allPromoDisclaimers}
                      isInSideBar={isInSideBar}
                    />
                  )}
                </Stack>
              </Stack.Item>
              <Stack.Item grow="true">
                <Stack alignX="end" gap="2">
                  <Stack alignX="end">
                    <Text size="BodyM" weight="bold">
                      {currency.euro(displayTotalPrice / 12)}
                    </Text>
                    <Text size="BodyS" color="textLowEmphasis" whiteSpace={'nowrap'}>
                      {fields.selectedProductsStep.perMonthLabel.value}
                    </Text>
                  </Stack>
                  <Stack alignX="end">
                    <Text size="BodyM">{currency.euro(displayTotalPrice)}</Text>
                    <Text size="BodyS" color="textLowEmphasis" whiteSpace={'nowrap'}>
                      {fields.selectedProductsStep.perYearLabel.value}
                    </Text>
                  </Stack>
                </Stack>
              </Stack.Item>
            </Stack>
            {isInSideBar && (
              <PromoBadges
                hasPriceSimulationPromo={hasPriceSimulationPromo}
                hasValidGezinsbondNumber={hasValidGezinsbondNumber}
                gezinsbondPromoBadgeText={fields.overviewStep.gezinsbondPromoBadgeText?.value}
                orderPromo={orderPromo ?? undefined}
                allPromoTexts={allPromoTexts}
                allPromoDisclaimers={allPromoDisclaimers}
                isInSideBar={isInSideBar}
              />
            )}
            <Stack.Item>
              <ClickableActions
                isBundle={isBundle}
                isInSidebar={isInSideBar}
                elecTariffUrl={elecTariffUrl}
                gasTariffUrl={gasTariffUrl}
                fields={fields}
                handleRemoveClick={handleRemoveClick}
                priceSimulationId={priceSimulationId}
                orderId={orderId}
                changeType={selectedProductName}
              />
            </Stack.Item>
          </Stack>
        </Bucket.Content>
      </Bucket>
    </>
  );
};

type ServiceInfo = {
  serviceName: 'boilerMaintenance' | 'serviceProposal';
  productName: string;
  product: OrderManagementWithItems['fields']['items'][number] | undefined;
};

const ServiceCard: FC<{
  serviceInfo: ServiceInfo;
  isInSideBar: boolean;
  fields: OrderManagementWithItems['fields'];
  format: ReturnType<typeof useFormatter>['format'];
  currency: ReturnType<typeof useFormatter>['currency'];
  setValue: ReturnType<typeof useFormContext<OrderManagementFormSchema>>['setValue'];
}> = ({ serviceInfo, isInSideBar, fields, format, currency, setValue }) => {
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [hasUpdateError, setHasUpdateError] = useState(false);
  const { serviceName, product, productName } = serviceInfo;
  const { mutate } = useSWRConfig();
  const orderId = useOrderId();

  const {
    selectedProducts,
    selectedServices,
    cacheKey: useSelectedProductsCacheKey,
  } = useSelectedProducts({
    orderId,
  });

  const title = product?.fields.data.title.value ?? '';
  const pricePerMonthVatInclusive = (product?.fields.data.pricePerMonthVatInclusiveText?.value ?? 0) as number;
  const pricePerMonthVatExclusive = (product?.fields.data.pricePerMonthVatExclusiveText?.value ?? 0) as number;

  const vatPercentage = getVatPercentage(pricePerMonthVatExclusive, pricePerMonthVatInclusive);

  const handleChangeClick = () => {
    if (serviceName === 'boilerMaintenance') {
      window.location.hash = `#${orderManagementSteps.boilerMaintenanceStep}`;
    } else if (serviceName === 'serviceProposal') {
      window.location.hash = `#${orderManagementSteps.serviceProposalStep}`;
    }
  };

  const handleRemoveClick = () => {
    setIsRemoveDialogOpen(true);
  };

  const handleConfirmRemoveServices = async () => {
    try {
      await clearSelectedService({
        removeBoilerMaintenance: serviceName === 'boilerMaintenance',
        removeServiceProposal: serviceName === 'serviceProposal',
        selectedServices,
        orderId: orderId ?? '',
        selectedProduct: selectedProducts,
        mutate,
        cacheKey: useSelectedProductsCacheKey,
        setValue,
      });
      setIsRemoveDialogOpen(false);
    } catch {
      setHasUpdateError(true);
    }
  };

  const handleDenyRemove = () => {
    setIsRemoveDialogOpen(false);
  };

  return (
    <>
      {hasUpdateError && <DataErrorNotification />}
      <AlertDialog
        isOpen={isRemoveDialogOpen}
        setOpen={setIsRemoveDialogOpen}
        title={fields.selectedProductsStep.removeProductDialog.value.title}
        description={fields.selectedProductsStep.removeProductDialog.value.content}
        confirmText={fields.selectedProductsStep.removeProductDialog.value.submitButtonText ?? 'Remove'}
        denyText={fields.selectedProductsStep.removeProductDialog.value.cancelButtonText ?? 'Cancel'}
        onConfirm={handleConfirmRemoveServices}
        onDeny={handleDenyRemove}></AlertDialog>
      <Stack gap="2">
        <Stack direction="row" gap="2" alignX="justify">
          <Stack direction="column">
            <Heading size="3XS" as="h3">
              {title}
            </Heading>
            <Text size="BodyS" color="textLowEmphasis">
              {format(fields.selectedProductsStep.vatInformationLabel.value, { amount: vatPercentage })}
            </Text>
          </Stack>
          <Stack.Item grow="true">
            <Stack direction="column" alignX="end">
              <Text size="BodyM" weight="bold">
                {currency.euro(pricePerMonthVatInclusive)}
              </Text>
              <Text size="BodyS" color="textLowEmphasis" whiteSpace={'nowrap'}>
                {fields.selectedProductsStep.perMonthLabel.value}
              </Text>
            </Stack>
          </Stack.Item>
        </Stack>
        <Stack direction="row" gap="2" alignX="justify">
          <Stack direction="row" gap="2">
            {!isInSideBar && <RemoveServiceLink fields={fields} onClick={handleRemoveClick} />}
          </Stack>
          <ChangeServiceLink fields={fields} changeType={productName} onClick={handleChangeClick} />
        </Stack>
      </Stack>
    </>
  );
};

export const SelectedServices: FC<{ isInSideBar?: boolean }> = ({ isInSideBar }) => {
  const { fields } = useContent<OrderManagementWithItems>();
  const { format, currency } = useFormatter();
  const { watch, setValue } = useFormContext<OrderManagementFormSchema>();

  const serviceProposal = watch('serviceProposal');
  const boilerMaintenance = watch('boilerMaintenance');

  const selectedServices: ServiceInfo[] = [];

  if (boilerMaintenance && boilerMaintenance !== 'none') {
    selectedServices.push({
      serviceName: 'boilerMaintenance',
      productName: boilerMaintenance,
      product: fields.items.find(item => item.name === boilerMaintenance),
    });
  }

  if (serviceProposal && serviceProposal !== 'none') {
    selectedServices.push({
      serviceName: 'serviceProposal',
      productName: serviceProposal,
      product: fields.items.find(item => item.name === serviceProposal),
    });
  }

  if (selectedServices.length === 0) {
    return null;
  }

  return (
    <Bucket title={fields.selectedProductsStep.selectedServicesTitle.value}>
      <Bucket.Content>
        <Stack direction="column" gap="8">
          {selectedServices.map((serviceInfo, index) => (
            <ServiceCard
              key={`${serviceInfo.serviceName}-${index}`}
              serviceInfo={serviceInfo}
              isInSideBar={isInSideBar ?? false}
              fields={fields}
              format={format}
              currency={currency}
              setValue={setValue}
            />
          ))}
        </Stack>
      </Bucket.Content>
    </Bucket>
  );
};
export const SelectedProductAndService: FC<{ isInSideBar?: boolean }> = ({ isInSideBar }) => {
  return (
    <>
      <SelectedProduct isInSideBar={isInSideBar} />
      <SelectedServices isInSideBar={isInSideBar} />
    </>
  );
};

const useProductData = ({
  selectedProducts,
  isBundle,
  hasLossOfPromo: hasLossOfPriceSimulationPromo,
  fields,
}: {
  selectedProducts?: PriceSimulationProductCombinationOptionDto;
  isBundle: boolean;
  hasLossOfPromo: boolean;
  fields: OrderManagementWithItems['fields'];
}) => {
  return useMemo(() => {
    const empty = {
      sitecoreBundle: undefined,
      sitecoreProduct: undefined,
      vatPercentage: 0,
      hasPriceSimulationPromo: false,
      totalPrice: 0,
    };
    if (!selectedProducts) return empty;

    const hasPriceSimulationPromo =
      selectedProducts.promoDisclaimers?.some(x => x.promoId) &&
      !hasLossOfPriceSimulationPromo &&
      (!!selectedProducts.totalYearlyPriceWithNonCondtitionalPromo || !!selectedProducts.totalConditionalPromo);

    let totalPrice = selectedProducts.totalYearlyPrice || 0;
    if (hasPriceSimulationPromo) {
      totalPrice = selectedProducts.totalYearlyPriceWithNonCondtitionalPromo ?? selectedProducts.totalYearlyPrice ?? 0;
    }
    if (isBundle) {
      const elecProduct = selectedProducts.electricityProduct;
      const gasProduct = selectedProducts.gasProduct;
      const getCost = (type: string) =>
        (elecProduct?.costs.find(c => c.costCategoryType === type)?.value ?? 0) +
        (gasProduct?.costs.find(c => c.costCategoryType === type)?.value ?? 0);
      const totalTaxes = getCost('TaxationCost');
      const vatPercentage = getVatPercentage(totalPrice - totalTaxes, totalPrice);
      const sitecoreBundle = getSitecoreBundle({
        technicalProductNameElec: elecProduct?.productName ?? '',
        technicalProductNameGas: gasProduct?.productName ?? '',
        enecoProductsBundlesList: fields.orderManagementProposal.enecoProductsBundlesList,
        enecoProductsList: fields.orderManagementProposal.enecoProductsList,
      });
      return { ...empty, sitecoreBundle, vatPercentage, hasPriceSimulationPromo, totalPrice };
    }

    const product = selectedProducts.electricityProduct ?? selectedProducts.gasProduct;
    const getCost = (type: string) => product?.costs.find(c => c.costCategoryType === type)?.value ?? 0;
    const totalTaxes = getCost('TaxationCost');
    const vatPercentage = getVatPercentage(totalPrice - totalTaxes, totalPrice);
    const sitecoreProduct = getSitecoreProduct({
      technicalProductName: product?.productName ?? '',
      enecoProductsList: fields.orderManagementProposal.enecoProductsList,
    });
    return { ...empty, sitecoreProduct, vatPercentage, hasPriceSimulationPromo, totalPrice };
  }, [
    fields.orderManagementProposal.enecoProductsBundlesList,
    fields.orderManagementProposal.enecoProductsList,
    hasLossOfPriceSimulationPromo,
    isBundle,
    selectedProducts,
  ]);
};

const ClickableActions: FC<{
  isBundle: boolean;
  isInSidebar: boolean;
  elecTariffUrl: string | undefined;
  gasTariffUrl: string | undefined;
  fields: OrderManagementWithItems['fields'];
  handleRemoveClick: () => void;
  priceSimulationId: string | undefined;
  orderId: string | undefined;
  changeType: string;
}> = ({
  isBundle,
  elecTariffUrl,
  gasTariffUrl,
  isInSidebar,
  fields,
  handleRemoveClick,
  priceSimulationId,
  orderId,
  changeType,
}) => {
  const isDesktop = useMediaQuery('lg');

  if (isInSidebar) {
    return (
      <Stack direction="row" alignX="end" gap="2">
        <ChangeProductLink
          fields={fields}
          priceSimulationId={priceSimulationId}
          orderId={orderId}
          changeType={changeType}
        />
      </Stack>
    );
  }
  if (isBundle && isDesktop) {
    return (
      <Stack direction="row" gap="2" alignX="justify">
        <Stack direction="row" gap="2">
          <RateCardLink
            href={elecTariffUrl ?? fields.selectedProductsStep.rateCardLink.value.href}
            target={fields.selectedProductsStep.rateCardElectricityLink.value.target}
            text={fields.selectedProductsStep.rateCardElectricityLink.value.text ?? 'Electriciteit [not in sitecore]'}
          />
          <RateCardLink
            href={gasTariffUrl ?? fields.selectedProductsStep.rateCardLink.value.href}
            target={fields.selectedProductsStep.rateCardGasLink.value.target}
            text={fields.selectedProductsStep.rateCardGasLink.value.text ?? 'Gas [not in sitecore]'}
          />
          <RemoveProductLink fields={fields} onClick={handleRemoveClick} />
        </Stack>
        <Stack direction="row" gap="2">
          <ChangeProductLink
            fields={fields}
            priceSimulationId={priceSimulationId}
            orderId={orderId}
            changeType={changeType}
          />
        </Stack>
      </Stack>
    );
  }
  if (isBundle && !isDesktop) {
    return (
      <Stack direction="column" gap={2}>
        <Stack direction="row" gap="2">
          <RateCardLink
            href={elecTariffUrl ?? fields.selectedProductsStep.rateCardLink.value.href}
            target={fields.selectedProductsStep.rateCardElectricityLink.value.target}
            text={fields.selectedProductsStep.rateCardElectricityLink.value.text ?? 'Electriciteit [not in sitecore]'}
          />
          <RateCardLink
            href={gasTariffUrl ?? fields.selectedProductsStep.rateCardLink.value.href}
            target={fields.selectedProductsStep.rateCardGasLink.value.target}
            text={fields.selectedProductsStep.rateCardGasLink.value.text ?? 'Gas [not in sitecore]'}
          />
        </Stack>
        <Stack direction="row" alignX="justify" gap="2">
          <RemoveProductLink fields={fields} onClick={handleRemoveClick} />
          <ChangeProductLink
            fields={fields}
            priceSimulationId={priceSimulationId}
            orderId={orderId}
            changeType={changeType}
          />
        </Stack>
      </Stack>
    );
  }
  if (!isBundle && isDesktop) {
    return (
      <Stack direction="row" gap="2" alignX="justify">
        <Stack direction="row" gap="2">
          <RateCardLink
            href={elecTariffUrl ?? gasTariffUrl ?? fields.selectedProductsStep.rateCardLink.value.href}
            target={fields.selectedProductsStep.rateCardLink.value.target}
            text={fields.selectedProductsStep.rateCardLink.value.text}
          />
          <RemoveProductLink fields={fields} onClick={handleRemoveClick} />
        </Stack>
        <ChangeProductLink
          fields={fields}
          priceSimulationId={priceSimulationId}
          orderId={orderId}
          changeType={changeType}
        />
      </Stack>
    );
  }
  if (!isBundle && !isDesktop) {
    return (
      <Stack direction="column" gap={2}>
        <Stack direction="row" gap="2">
          <RateCardLink
            href={elecTariffUrl ?? gasTariffUrl ?? fields.selectedProductsStep.rateCardLink.value.href}
            target={fields.selectedProductsStep.rateCardLink.value.target}
            text={fields.selectedProductsStep.rateCardLink.value.text}
          />
        </Stack>
        <Stack direction="row" gap="2" alignX="justify">
          <RemoveProductLink fields={fields} onClick={handleRemoveClick} />
          <ChangeProductLink
            fields={fields}
            priceSimulationId={priceSimulationId}
            orderId={orderId}
            changeType={changeType}
          />
        </Stack>
      </Stack>
    );
  }

  return null;
};
